import React, { ReactElement } from 'react';
import { SUPPLIER_WORD_COUNT_PROPS, SUPPLIER_WORD_COUNT_STATE } from "./Types";
import { Component } from "puzzle";
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridRowProps,
} from "@progress/kendo-react-grid";
import _ from "lodash";
import uuid from "uuid-random";
import classNames from "classnames";
import { SUBMISSION } from "../../../../Abstract/Grid/Types";

class SupplierWordCount extends Component<SUPPLIER_WORD_COUNT_PROPS, SUPPLIER_WORD_COUNT_STATE> {

  static defaultProps: SUPPLIER_WORD_COUNT_PROPS = {
    rowHeight: 40,
    translatableHeaders: [
      "name",
      "inContextMatch",
      "repetitions",
      "match100",
      "noMatch",
      "total"
    ]
  };

  public state: SUPPLIER_WORD_COUNT_STATE = {
    data: [],
    columns: [],
    totalValues: [],
    totalWordCount: 0,
    target: ''
  };

  /**
   * Getter for submissionData
   */
  getSubmissionData = (): SUBMISSION => {
    return this.props.submissionData;
  }

  /**
   * Grid cell render
   * @param defaultRendering
   * @param cellProps
   */
  cellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps):
    JSX.Element => {
    const {
      dataItem,
      field
    } = cellProps;


    if (defaultRendering && field) {
      let children;
      let editedProps = {
        ...defaultRendering.props
      };

      if (field === 'total' || field === 'name') {
        children = _.get(dataItem, field);
        if (field === 'name' && 'canceledTmStatistics' in dataItem) {
          children = <>
            <span className={'tp-cancelled-tag'}>
              {this.translate('cancelled')}
            </span>
            <span>
              {children}
            </span>
          </>
        }
      } else {
        if ('canceledTmStatistics' in dataItem) {
          children = _.find(dataItem.canceledTmStatistics, { 'name': field }).wordCount
        } else {
          children = _.find(dataItem.cumulativeTmStatistics, { 'name': field }).wordCount
        }
      }

      if (children === null) {
        children = this.translate('nonParsableWordCount')
      }

      return React.cloneElement(defaultRendering, editedProps, children);
    }

    return <></>
  };

  /**
   * Creates grid columns
   */
  createColumns = (): React.ReactElement[] => {
    const {
      columns
    } = this.state;

    return columns.map((columnName: string, index: number) => {
      let columnProps = {
        key: uuid(), //remove for test snapshot
        field: columnName,
        filterable: false,
        title: _.includes(this.props.translatableHeaders, columnName) ? this.translate(`columnHeaders.${columnName}`) : columnName,
        width: '180px',
        footerCell: this.footerRender.bind(this, index - 1),
        ...(columnName === 'total' && {
            headerClassName: 'totalHeaderClass'
          }
        )
      }

      return <Column
        {...columnProps}
      />
    });
  };

  /**
   * Row renderer, add class for cancelled files
   */
  rowRender = (trElement: ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) => {
    const elementProps = { ...trElement.props };
    const dataItem = rowProps.dataItem;

    if ('canceledTmStatistics' in dataItem) {
      elementProps.className += ' tp-row-cancelled';
    }

    const trProps = {
      ...elementProps
    };

    return React.cloneElement(trElement, { ...trProps }, elementProps.children);
  }

  /**
   * Sets class data.
   * @param data
   */
  setData = (data: any): void => {
    const responseData: any = data

    let updatedData = responseData.jobPhase.data;
    let target: string;
    let totalValues: any;
    let docs: any = Object.values(updatedData).map((item: any, index: number) => {
      let name = Object.keys(updatedData)[index]
      return { ...item, name: name, total: item.totalWordCount }
    });

    let values: any = {};
    Object.keys(responseData.jobPhase.statistics).forEach((key: string) => {
      if (Array.isArray(responseData.jobPhase.statistics[key])) {
        values = { ...values, [key]: responseData.jobPhase.statistics[key] }
      }
    })

    target = 'cumulativeTmStatistics' in responseData.jobPhase.statistics ? 'cumulativeTmStatistics' : 'canceledTmStatistics'

    totalValues = values[target].map((item: any, index: any) => {
      if (values.canceledTmStatistics) {
        return { ...item, wordCount: item.wordCount - values.canceledTmStatistics[index].wordCount }
      } else {
        return item
      }
    })

    let returnData: any = {
      batchName: responseData.jobPhase.statistics.batchName,
      language: responseData.jobPhase.statistics.targetLanguageDisplayName,
      phase: responseData.jobPhase.statistics.phaseName,
      documents: docs
    };

    let columnNames: string[] = []
    returnData.documents.map((item: any) => {
      if (!item.cumulativeTmStatistics) {
        return columnNames = item.canceledTmStatistics.map((secondItem: any) => secondItem.name)
      } else {
        return columnNames = item.cumulativeTmStatistics.map((secondItem: any) => secondItem.name)
      }
    })

    const totalWordCount = _.sumBy(totalValues, 'wordCount')

    this.setState({
      data: returnData.documents,
      columns: ['name', ...columnNames, 'total'],
      totalWordCount,
      totalValues,
    });
  };


  /**
   * Cell footer render
   * @param index
   * @param props
   */
  footerRender = (index: number, props: any): React.ReactElement => {
    let value: number | string = 0;
    let className = classNames(
      'grid-footer-cell tp-text-semibold', {
        'total tp-color-accent': props.field === 'total'
      });

    props = { ...props, 'aria-colindex': props['ariaColumnIndex']}
    delete props['ariaColumnIndex'];

    if (this.state.totalValues[index]) {
      value = this.state.totalValues[index].wordCount
    }
    if (props.field === 'name') {
      value = this.translate('totalCountText');
    }
    if (props.field === 'total') {
      value = this.state.totalWordCount
    }


    const tdProps = _.omit(props, ['field']);

    return (
      <td
        {...tdProps}
        className={className}
      >
        {value}
      </td>
    );
  };

  renderBody = () => {
    const {
      data,
    } = this.state;
    const {
      rowHeight
    } = this.props;

    return (
      <>
        <Grid
          resizable={false}
          rowRender={this.rowRender}
          rowHeight={rowHeight}
          data={data}
          cellRender={this.cellRender}
        >
          {this.createColumns()}
        </Grid>
      </>
    );
  }
}

export default SupplierWordCount;
